import { MODE_CLIENT, MODE_GENERAL, MODE_PROVIDER } from "@/lib/dany";

/**
 * Page context defines the FAB's initial mode (DANY.md §2.5, point 4).
 *
 * The mode is only ever a HINT — `DanyContext.resolved_mode()` on the backend
 * lets a signed-in account's real role win. So this table can be generous: the
 * worst case for a wrong guess is that Dany opens with provider-flavoured
 * suggestions for someone who then asks a client question, and the model
 * follows the question.
 *
 * Ordered longest-prefix-first; the first match wins.
 */
const ROUTES = [
  // Provider acquisition and everything behind the FixyBee dashboard.
  ["/become-a-fixybee", MODE_PROVIDER],
  ["/pricing", MODE_PROVIDER],
  ["/open-jobs", MODE_PROVIDER],
  ["/provider", MODE_PROVIDER],
  ["/pro/", MODE_PROVIDER],

  // Client surfaces.
  ["/client", MODE_CLIENT],
  ["/providers", MODE_CLIENT],
  ["/guides/post-a-job", MODE_CLIENT],

  // Everything public and audience-neutral stays general: the home, FAQ, legal
  // pages, how-it-works, guides index.
];

export function modeForPath(pathname = "/") {
  const path = String(pathname || "/");
  for (const [prefix, mode] of ROUTES) {
    if (path === prefix || path.startsWith(prefix)) return mode;
  }
  return MODE_GENERAL;
}

/**
 * Where the FAB must not appear.
 *
 * Three reasons only, and none is taste:
 *   - the conversational surface itself, where a button that opens it is noise;
 *   - flows where a floating control overlaps the thing the user came to do
 *     (auth forms, Stripe's payment landing);
 *   - Messages, both the list and a conversation (Jorge, 2026-09-09).
 *     Coordinating a job with the other human is not a context Dany has
 *     anything to add to, and on a phone the gold circle sat next to the send
 *     button asking to be pressed for something else — the confusion Daniel
 *     reported, which the composer's charcoal send button only half solved.
 * Admin is included because it is an internal console, not a product surface.
 */
const SUPPRESSED = [
  "/dany",
  "/login",
  "/signup",
  // The phone code (REG): a one-errand screen, like the sign-up it came from.
  "/provider/verify-phone",
  "/auth/callback",
  "/forgot-password",
  "/reset-password",
  "/pay/",
  "/admin",
  "/client/messages",
  "/provider/messages",
  // The same page outside the dashboard's navigation. It is still reachable —
  // old links and anything that has not been updated to the namespaced route.
  "/chat",
];

export function fabSuppressedOn(pathname = "/") {
  const path = String(pathname || "/");
  return SUPPRESSED.some((prefix) => path === prefix || path.startsWith(prefix));
}

export default modeForPath;
